import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import type { FilingVersionSnapshot } from '../../types';
import { formatCurrency, formatDate, formatWholeDay } from '../../utils/complianceFormatters';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  emptyState: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  table: {
    width: '100%',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

interface CurrentVersion {
  confirmationNumber?: string;
  amountDue?: number | null;
  bankDebitAmount?: number | null;
  bankDebitDate?: string;
  bankDebitAccountMasked?: string;
  filedAt?: string;
  filedBy?: string;
}

interface Props {
  currentVersion: number;
  current: CurrentVersion;
  priorVersions: FilingVersionSnapshot[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Render bank debit cell: amount + masked account (+ date if present). */
function formatBankDebit(
  amount?: number | null,
  accountMasked?: string,
  date?: string,
): string {
  if (amount === undefined && !accountMasked) return '—';
  const parts: string[] = [];
  if (amount !== undefined && amount !== null) parts.push(formatCurrency(amount));
  if (accountMasked) parts.push(accountMasked);
  if (date) parts.push(formatWholeDay(date));   // the debit date: a day, not an instant
  return parts.length > 0 ? parts.join(' · ') : '—';
}

/** Render filed cell: date + optional "by" name. */
function formatFiled(filedAt?: string, filedBy?: string): string {
  if (!filedAt) return '—';
  const date = formatDate(filedAt);
  return filedBy ? `${date} · ${filedBy}` : date;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function FilingVersionHistory({
  currentVersion,
  current,
  priorVersions,
}: Props) {
  const { t } = useTranslation();
  const styles = useStyles();

  // Sort priors newest-first (highest versionNumber first)
  const sortedPriors = [...priorVersions].sort(
    (a, b) => b.versionNumber - a.versionNumber,
  );

  return (
    <div className={styles.root}>
      <Table className={styles.table} size="small">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>{t('filingVersionHistory.columns.version')}</TableHeaderCell>
            <TableHeaderCell>{t('filingVersionHistory.columns.confirmation')}</TableHeaderCell>
            <TableHeaderCell>{t('filingVersionHistory.columns.amount')}</TableHeaderCell>
            <TableHeaderCell>{t('filingVersionHistory.columns.bankDebit')}</TableHeaderCell>
            <TableHeaderCell>{t('filingVersionHistory.columns.filed')}</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* Current (live) version — always first */}
          <TableRow>
            <TableCell>{t('filingVersionHistory.current', { version: currentVersion })}</TableCell>
            <TableCell>{current.confirmationNumber ?? '—'}</TableCell>
            <TableCell>{formatCurrency(current.amountDue)}</TableCell>
            <TableCell>
              {formatBankDebit(
                current.bankDebitAmount,
                current.bankDebitAccountMasked,
                current.bankDebitDate,
              )}
            </TableCell>
            <TableCell>{formatFiled(current.filedAt, current.filedBy)}</TableCell>
          </TableRow>

          {/* Prior snapshots, newest-first */}
          {sortedPriors.map((snap) => (
            <TableRow key={snap.versionNumber}>
              {/* i18n-ignore: a version tag reads the same in both languages */}
              <TableCell>{`v${snap.versionNumber}`}</TableCell>
              <TableCell>{snap.confirmationNumber ?? '—'}</TableCell>
              <TableCell>{formatCurrency(snap.amountDue)}</TableCell>
              <TableCell>
                {formatBankDebit(
                  snap.bankDebitAmount,
                  snap.bankDebitAccountMasked,
                  snap.bankDebitDate,
                )}
              </TableCell>
              <TableCell>{formatFiled(snap.filedAt, snap.filedBy)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {priorVersions.length === 0 && (
        <span className={styles.emptyState}>{t('filingVersionHistory.empty')}</span>
      )}
    </div>
  );
}
